import React, { useState, useEffect } from "react";
import api from "../../../../utils/api";
import { copyText } from "../excelData";
import { Chip, EmailChips, Sub, fmtD, fmtDT, uniq } from "./ui";

const errMsg = (e, fallback) => e.response?.data?.error || fallback;

// "Send to next in line": queues an email (nothing is sent from here), logs a HANDOFF_SENT
// event, optionally moves the pallet to a stage and remembers the team's To / CC
export default function HandoffPanel({ p, stages, teams, contacts, outbox, ry, onDone, toast }) {
    const c = p.custody;
    const nextIdx = stages.findIndex(s => s.key === c.nextStage);
    const startTeam = c.nextTeam || "Customer Care";
    const [team, setTeam] = useState(startTeam);
    const [stage, setStage] = useState(c.nextStage || "");
    const [to, setTo] = useState((contacts[startTeam]?.to || []).slice(0, 2));
    const [cc, setCc] = useState(contacts[startTeam]?.cc || []);
    const [touched, setTouched] = useState(false);
    const [asComment, setAsComment] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const stageLabel = key => stages.find(s => s.key === key)?.label;
    const defaultSubject = () => `Pallet ${p.customer} · ${p.program}: over to ${team}`;
    const defaultBody = () => [
        `Hi ${team},`, "",
        `${p.customer} · ${p.program} (${p.palletQty} pallets, ${p.bom?.components} SKUs) is ready for your action${stage ? `: ${stageLabel(stage)}` : ""}.`, "",
        `Where it is: ${p.popup?.headline || "—"}`,
        `Was with: ${c.sitsWith}. Waiting for ${c.waitingFor}${c.daysLate > 0 ? ` (${c.daysLate} days late)` : ""}.`,
        `DC date: ${fmtD(p.dcDate, ry)} · Delivery to IVE: ${fmtD(p.deliveryDateToIve, ry)} · Create STO: ${fmtD(p.createStoDate, ry)}`,
        p.refs?.stoNumber ? `STO ${p.refs.stoNumber}${p.refs.fgPoNumber ? ` · FG PO ${p.refs.fgPoNumber}` : ""}` : "STO not raised yet",
        `Open issues: ${p.popup?.openIssues ?? 0}`, "",
        `Open in Control Tower: /control-tower?view=pallets&pallet=${p.id}`,
    ].join("\n");
    const [subject, setSubject] = useState(defaultSubject());
    const [body, setBody] = useState(defaultBody());

    // Re-fill the text when the team or stage changes, unless the user has edited it
    useEffect(() => {
        if (!touched) { setSubject(defaultSubject()); setBody(defaultBody()); }
        // eslint-disable-next-line
    }, [team, stage]);

    const pickTeam = t => {
        setTeam(t);
        setTo((contacts[t]?.to || []).slice(0, 2));
        setCc(contacts[t]?.cc || []);
        const st = stages.find((s, i) => s.team === t && i >= Math.max(0, nextIdx));
        if (st && !touched) setStage(st.key);
    };
    const allKnown = uniq(Object.values(contacts).flatMap(x => [...x.to, ...x.cc]));

    const submit = async e => {
        e.preventDefault();
        if (!to.length) { setErr("Add at least one To address."); return; }
        setBusy(true); setErr("");
        try {
            await api.post(`/api/excel-ingestion/pallets/${encodeURIComponent(p.id)}/handoff`, { toTeam: team, toStage: stage || null, to, cc, subject, body, asComment });
            toast(`Email queued to ${to.length + cc.length} recipient${to.length + cc.length > 1 ? "s" : ""}. ${team} contacts saved${stage ? `; pallet moved to ${stageLabel(stage)}` : ""}.`);
            setTouched(false);
            onDone();
        } catch (ex) {
            setErr(errMsg(ex, "Could not queue the email"));
        } finally {
            setBusy(false);
        }
    };

    const copyMail = m => {
        const t = `To: ${m.to.join(", ")}\n${m.cc?.length ? `CC: ${m.cc.join(", ")}\n` : ""}Subject: ${m.subject}\n\n${m.bodyText}`;
        copyText(t).then(() => toast("Email copied"), () => toast("Copy was blocked here. Select the text in the message box instead."));
    };
    const mailto = m => `mailto:${m.to.join(",")}?${m.cc?.length ? `cc=${encodeURIComponent(m.cc.join(","))}&` : ""}subject=${encodeURIComponent(m.subject)}&body=${encodeURIComponent(m.bodyText)}`;

    const field = "w-full bg-[#0D1520] border border-[#1F2937] rounded-lg px-2.5 py-1.5 text-[12px] text-slate-200 focus:border-blue-500/50 outline-none";
    return (
        <section className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-3" aria-labelledby="ho-h">
            <div>
                <h3 id="ho-h" className="text-sm font-bold text-white">Send to next in line</h3>
                <Sub>Queues a notification email only. Nothing changes in SAP or with the carrier.</Sub>
            </div>
            <div className="flex items-end gap-3">
                <div className="min-w-0"><Sub>From</Sub><b className="block text-[12px] text-slate-100">{c.sitsWith}</b><Sub>{c.stageLabel}</Sub></div>
                <span className="text-slate-500 pb-4" aria-hidden="true">→</span>
                <div className="flex-1 min-w-0">
                    <label htmlFor="ho-team" className="text-[11px] text-slate-400">To team</label>
                    <select id="ho-team" value={team} onChange={e => pickTeam(e.target.value)} className={field}>
                        {teams.map(t => <option key={t} value={t}>{t === "Customer" ? p.customer : t}</option>)}
                    </select>
                </div>
            </div>
            <form onSubmit={submit} noValidate className="space-y-3">
                <div className="space-y-1">
                    <label htmlFor="ho-stage" className="text-[11px] text-slate-400">Move pallet to stage</label>
                    <select id="ho-stage" value={stage} onChange={e => setStage(e.target.value)} className={field}>
                        <option value="">Notify only (stage stays {c.stageLabel})</option>
                        {stages.map(s => <option key={s.key} value={s.key}>{s.label} · {s.key === "CUSTOMER_GR" ? p.customer : s.party}</option>)}
                    </select>
                </div>
                <EmailChips id="ho-to" label="To" value={to} onChange={v => { setTo(v); setErr(""); }} suggestions={uniq([...(contacts[team]?.to || []), ...allKnown])} placeholder="name@company.com"/>
                <EmailChips id="ho-cc" label="CC" value={cc} onChange={setCc} suggestions={uniq([...(contacts[team]?.cc || []), ...allKnown])} placeholder="Optional"/>
                <div className="space-y-1">
                    <label htmlFor="ho-sub" className="text-[11px] text-slate-400">Subject</label>
                    <input id="ho-sub" value={subject} maxLength={200} onChange={e => { setSubject(e.target.value); setTouched(true); }} className={field}/>
                </div>
                <div className="space-y-1">
                    <label htmlFor="ho-body" className="text-[11px] text-slate-400">Message</label>
                    <textarea id="ho-body" rows={9} value={body} onChange={e => { setBody(e.target.value); setTouched(true); }} className={`${field} resize-y font-mono text-[11px]`}/>
                </div>
                <label className="flex items-center gap-2 text-[11px] text-slate-300">
                    <input type="checkbox" checked={asComment} onChange={e => setAsComment(e.target.checked)}/> Also post this message to the comments
                </label>
                {err && <p className="text-[11px] text-red-400" role="alert">{err}</p>}
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setTouched(false); setSubject(defaultSubject()); setBody(defaultBody()); }}
                        className="px-3 py-1.5 rounded-lg border border-[#374151] text-[12px] text-slate-300 hover:border-slate-500">Reset text</button>
                    <button type="submit" disabled={busy} className="px-3 py-1.5 rounded-lg bg-blue-600 text-[12px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                        {busy ? "Queuing…" : "Queue email"}
                    </button>
                </div>
            </form>
            {outbox.length > 0 && (
                <details className="border-t border-[#1F2937] pt-3">
                    <summary className="text-[12px] text-slate-300 cursor-pointer">Emails for this pallet ({outbox.length})</summary>
                    <ul className="mt-2 space-y-2">
                        {outbox.slice(0, 12).map(m => (
                            <li key={m.id} className="bg-[#0D1520] border border-[#1F2937] rounded-lg p-2.5 space-y-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <Chip tone={m.ruleId === "MANUAL_HANDOFF" ? "info" : "muted"}>{m.ruleId === "MANUAL_HANDOFF" ? "hand-off" : String(m.ruleId || "").replace("R_", "").replace(/_/g, " ").toLowerCase()}</Chip>
                                    <Chip>{m.status}</Chip>
                                    <Sub>{m.delivery === "dailyDigest" ? `digest ${fmtDT(m.sendAfter)}` : "immediate"}</Sub>
                                </div>
                                <div className="text-[12px] text-slate-200">{m.subject}</div>
                                <Sub>To {m.to.join(", ")}{m.cc?.length ? ` · CC ${m.cc.join(", ")}` : ""}</Sub>
                                <div className="flex gap-2 pt-1">
                                    <button type="button" onClick={() => copyMail(m)} className="px-2 py-1 rounded border border-[#374151] text-[11px] text-slate-300 hover:border-slate-500">Copy email</button>
                                    <a href={mailto(m)} className="px-2 py-1 rounded border border-[#1F2937] text-[11px] text-slate-400 hover:text-white">Open in mail app</a>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <Sub className="mt-2">Emails are queued in the Control Tower outbox; nothing is sent automatically yet.</Sub>
                </details>
            )}
        </section>
    );
}
